/*
 * Copyright (C) 2024 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import { InvalidParam } from '@common/tech-shared';
import { Component, OnDestroy, OnInit, Optional, Self } from '@angular/core';
import { ControlValueAccessor, NgControl, UntypedFormControl } from '@angular/forms';
import { Subscription } from 'rxjs';

@Component({
  template: 'NO UI',
  standalone: false,
})
export abstract class FormControlEditorAbstractComponent implements ControlValueAccessor, OnInit, OnDestroy {
  readonly fieldControl: UntypedFormControl = new UntypedFormControl();
  public onChange = (value: unknown) => undefined;
  public onTouched = () => undefined;
  public invalidParams: InvalidParam[] = [];

  _changesSubscription: Subscription;
  _statusSubscription: Subscription;

  constructor(@Self() @Optional() public control: NgControl | null) {
    if (this.control) this.control.valueAccessor = this;
  }

  ngOnInit(): void {
    this._changesSubscription = this.fieldControl.valueChanges.subscribe((value: unknown) =>
      this.handleFieldControlValueChange(value),
    );

    if (this.control) {
      this._statusSubscription = this.control.statusChanges.subscribe(() => {
        this.setErrors();
      });
    }
  }

  handleFieldControlValueChange(value: unknown): void {
    this.onChange(value);
    this._removeErrors();
  }

  touch(): void {
    this.onTouched();
  }

  writeValue(text: string): void {
    this.fieldControl.setValue(text);
  }

  registerOnChange(fn: (text: string | Date) => {}): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => {}): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) /* NOSONAR */ {
      this.fieldControl.disable();
    } else {
      this.fieldControl.enable();
    }
  }

  ngOnDestroy(): void {
    if (this._changesSubscription) this._changesSubscription.unsubscribe();
    if (this._statusSubscription) this._statusSubscription.unsubscribe();
  }

  setErrors(): void {
    if (!this.control) return;

    this.fieldControl.setErrors(this.control.errors);
    this.fieldControl.markAsTouched();

    this._updateInvalidParams();
  }

  _removeErrors(): void {
    if (!this.control) return;

    this.fieldControl.setErrors(null);
    this._updateInvalidParams();
  }

  _updateInvalidParams(): void {
    this.invalidParams =
      this.fieldControl.errors ?
        Object.keys(this.fieldControl.errors).map((key) => <InvalidParam>this.fieldControl.errors[key])
      : [];
  }
}
